// Barratt API - Delivery Appeals, Spend Leakage, Bulk Upload, Commodity Codes
// + Barratt Standalone Module (Category/Analyse BI, Division Performance, Call Off, Data Upload)
import api, { type AxiosResponse, type PaginatedResponse } from './client';

// ============================================================================
// Types
// ============================================================================

export interface FeatureFlags {
  /** Isolated Part Schedule module; per-customer CompanySettings override. Absent = platform default (shown). */
  part_schedule?: boolean;
  delivery_appeals: boolean;
  spend_leakage: boolean;
  bulk_upload: boolean;
  commodity_codes: boolean;
  supplier_stock_levels: boolean;
  switch_call_off_tab: boolean;
  /** The viewer's supplier company is a customer's Logistics Provider: its
   *  Call-Off tab shows the carrier view instead of the supplier screens. */
  logistics_view: boolean;
  allow_supplier_decline: boolean;
  po_line_schedules: boolean;
  schedule_collaboration: boolean;
  show_forecast_total: boolean;
  /** Forecast grid shows only the weekly horizon (hide month + future columns). */
  forecast_weeks_only: boolean;
  /** Forecast grid + export hide the Supplier Part No column. */
  forecast_hide_supplier_part_no: boolean;
  /** Forecast description comes from the part master, over forecast / PO-line text. */
  forecast_description_from_master: boolean;
  /** PNA-only: enables the "Produmax Export" button on the ASN detail modal. */
  asn_produmax_export: boolean;
  /**
   * CR-6160: paid ASN logistics-tracking module (external courier tracking).
   * Platform-level, Helix-admin only (Platform Admin → Purchasing). Default OFF.
   * When ON, ASN courier + tracking become required and live delivery status is
   * pulled from the tracking provider; when OFF, tracking is fully disabled.
   */
  asn_logistics_tracking: boolean;
  /**
   * CR-6274: GRN Repair module switch (Platform Admin → Features & Modules).
   * Absent = ON, so installs that predate the toggle keep the toolbox. When
   * OFF every GRN Repair entry point is hidden and the backend refuses the
   * repair endpoints. A Scheduled-enabled install remains a precondition on
   * top of this, because the toolbox repairs GRN-to-schedule-slot allocations.
   */
  grn_repair?: boolean;
  // Portal mode set by PLATFORM_MODE env var or Admin Setup > Features
  // CR-5334: 'barratt' renamed to neutral 'spend_analytics'; legacy value kept for reads.
  portal_mode?: 'standard' | 'spend_analytics' | 'compliance' | 'customer_portal' | 'barratt';
  // Dashboard tab visibility (CR-4441)
  dashboard_tabs?: Record<string, boolean>;
  supplier_dashboard_tabs?: Record<string, boolean>;
}

export interface DeliveryAppeal {
  id: number;
  appeal_number: string;
  po_line: number;
  po_number?: string;
  po_line_number?: number;
  part_number?: string;
  description?: string;
  appeal_type: string;
  appeal_type_display?: string;
  status: string;
  status_display?: string;
  original_delivery_date: string;
  actual_delivery_date?: string;
  proposed_date?: string;
  reason: string;
  supporting_evidence?: string;
  reviewer_notes?: string;
  rejection_reason?: string;
  created_by?: number;
  created_by_name?: string;
  reviewed_by?: number;
  reviewed_by_name?: string;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  reviewed_at?: string;
  attachments?: DeliveryAppealAttachment[];
  logs?: DeliveryAppealLog[];
}

export interface DeliveryAppealAttachment {
  id: number;
  appeal: number;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type?: string;
  uploaded_by?: number;
  uploaded_by_name?: string;
  uploaded_at: string;
}

export interface DeliveryAppealLog {
  id: number;
  appeal: number;
  action: string;
  old_status?: string;
  new_status?: string;
  notes?: string;
  performed_by?: number;
  performed_by_name?: string;
  performed_at: string;
}

export interface DeliveryAppealStats {
  total: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
}

export interface SpendLeakageReport {
  id: number;
  report_number: string;
  title: string;
  description?: string;
  status: string;
  status_display?: string;
  period_start: string;
  period_end: string;
  total_spend: number;
  contracted_spend: number;
  leakage_spend: number;
  leakage_percentage: number;
  potential_savings: number;
  company?: number;
  company_name?: string;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
  items?: SpendLeakageItem[];
}

export interface SpendLeakageItem {
  id: number;
  report: number;
  po_number?: string;
  po_line?: number;
  supplier?: number;
  supplier_name?: string;
  supplier_display?: string;
  category: string;
  category_display?: string;
  description: string;
  spend_amount: number;
  contracted_price?: number;
  variance_amount: number;
  transaction_date: string;
  notes?: string;
  action_taken?: string;
}

export interface SpendLeakageDashboard {
  total_leakage: number;
  report_count: number;
  recent_reports: SpendLeakageReport[];
}

export interface CommodityCode {
  id: number;
  code: string;
  name: string;
  description?: string;
  parent?: number;
  parent_name?: string;
  level: number;
  children_count?: number;
  full_path?: string;
  company?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  children?: CommodityCode[];
}

// ============================================================================
// Dataset Types (Barratt BI System)
// ============================================================================

export interface Dataset {
  id: number;
  name: string;
  ds_type: 'invoice' | 'supplier_performance' | 'division_performance' | 'quality' | 'custom';
  ds_type_display?: string;
  data_source: 'upload' | 'api' | 'manual';
  data_source_display?: string;
  description?: string;
  is_enabled: boolean;
  company?: number;
  company_name?: string;
  owner?: number;
  owner_name?: string;
  icon_content?: string;
  icon_extension?: string;
  import_number?: number;
  last_data_update?: string;
  fields?: DatasetField[];
  record_count?: number;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface DatasetField {
  id: number;
  dataset: number;
  bind_field: string;
  title: string;
  field_type:
    | 'string'
    | 'integer'
    | 'decimal'
    | 'date'
    | 'datetime'
    | 'boolean'
    | 'lookup'
    | 'percentage'
    | 'currency'
    | 'time'
    | 'document'
    | 'link';
  field_type_display?: string;
  is_enabled: boolean;
  is_key_field: boolean;
  is_filter: boolean;
  filter_type: 'none' | 'dropdown' | 'search' | 'date_range' | 'number_range';
  filter_type_display?: string;
  is_default_view: boolean;
  is_sortable: boolean;
  align_type: 'left' | 'center' | 'right';
  use_thousand_separator: boolean;
  decimal_places: number;
  date_format: string;
  char_length?: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DatasetRecord {
  id: number;
  data: Record<string, unknown>;
  created_at: string;
}

// ============================================================================
// Analyse Category & Dashboard Types (Barratt Standalone Module)
// ============================================================================

export interface AnalyseCategory {
  id: number;
  name: string;
  ds_type: 'standard' | 'barratt';
  ds_type_display?: string;
  hint?: string;
  company?: number;
  sort_order: number;
  icon_content?: string;
  icon_extension?: string;
  icon_version?: number;
  original_category?: number;
  created_by?: number;
  created_by_name?: string;
  dashboard_count?: number;
  created_at: string;
  updated_at: string;
}

export interface AnalyseDashboard {
  id: number;
  name: string;
  description?: string;
  is_enabled: boolean;
  category?: number;
  category_name?: string;
  dataset?: number;
  dataset_name?: string;
  is_filter_board: boolean;
  is_advanced_filter_board: boolean;
  filter_field?: number;
  shared_with: 'private' | 'company' | 'company_and_suppliers';
  shared_with_display?: string;
  level: 1 | 2 | 3;
  level_display?: string;
  download_lock: boolean;
  company?: number;
  sort_order: number;
  app_code?: string;
  indicator_count?: number;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface DashboardIndicator {
  id: number;
  category: number;
  category_name?: string;
  category_dashboard?: number;
  name: string;
  chart_type: 'bar' | 'line' | 'pie' | 'donut' | 'waterfall' | 'heatmap' | 'summary' | 'grid' | 'kpi' | 'indicator' | 'geolocation' | 'markup';
  chart_type_display?: string;
  hint?: string;
  config: Record<string, unknown>;
  dataset?: number;
  dataset_name?: string;
  shape_key?: number;
  icon_content?: string;
  icon_extension?: string;
  icon_version?: number;
  original_indicator_id?: number;
  original_dashboard_id?: number;
  company?: number;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
}

// ============================================================================
// Import/Upload Types
// ============================================================================

export interface ImportSummary {
  id: number;
  dataset: number;
  dataset_name?: string;
  template?: number;
  template_display?: string;
  company?: number;
  company_name?: string;
  user?: number;
  user_name?: string;
  total_records: number;
  total_pages: number;
  imported_pages: number;
  imported_pages_success: number;
  page_size: number;
  is_success: boolean;
  is_complete: boolean;
  completed_by_manual: boolean;
  is_create_fields: boolean;
  summary_type: 1 | 14;
  summary_type_display?: string;
  progress_percentage?: number;
  log_count?: number;
  total_added?: number;
  total_failed?: number;
  has_errors?: boolean;
  created_at: string;
  completed_at?: string;
}

export interface ImportLog {
  id: number;
  import_summary: number;
  company?: number;
  company_name?: string;
  user?: number;
  user_name?: string;
  page_index: number;
  is_success: boolean;
  import_count: number;
  added: number;
  force_added: number;
  updated: number;
  skipped: number;
  failed: number;
  key_field_empty: number;
  repeat: number;
  deleted: number;
  empty_row: number;
  merge_field_not_found: number;
  calculated_failed: number;
  description?: string;
  push_time?: string;
  created_at: string;
  finish_time?: string;
  exception_count?: number;
  success_rate?: number;
  exceptions?: ImportException[];
}

export interface ImportException {
  id: number;
  import_log: number;
  row_number: number;
  column_name?: string;
  severity: 'error' | 'warning' | 'info';
  severity_display?: string;
  error_code?: string;
  message: string;
  raw_data: Record<string, string>;
  created_at: string;
}

export interface ImportTemplate {
  id: number;
  name: string;
  description: string;
  columns: string[];
}

// ============================================================================
// Division Performance Types
// ============================================================================

export interface DivisionPerformanceScore {
  id: number;
  customer: number;
  customer_name?: string;
  supplier: number;
  supplier_name?: string;
  performance_date: string;
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  quarter_display?: string;
  year: number;
  score: 1 | 2 | 3 | 4 | 5;
  display_score?: number; // score * 20 (20/40/60/80/100)
  comments?: string;
  include_in_division_score: boolean;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface QuarterCompletion {
  id: number;
  customer: number;
  customer_name?: string;
  quarter: string;
  year: number;
  is_completed: boolean;
  completed_at?: string;
  completed_by?: number;
  completed_by_name?: string;
}

export interface QuarterInfo {
  value: string;
  label: string;
}

export interface ScoreReportRow {
  supplier: string;
  scores: Record<string, { score: number; display_score: number; comments?: string }>;
  average_score: number;
  average_display_score: number;
}

// ============================================================================
// Call Off Performance Types
// ============================================================================

export interface CallOffPerformanceData {
  id: number;
  dataset: number;
  dataset_name?: string;
  customer: number;
  customer_name?: string;
  supplier: number;
  supplier_name?: string;
  required_date: string;
  call_off_date: string;
  week_interval: number;
  additional_data: Record<string, unknown>;
  created_at: string;
}

export interface CallOffAggregatedData {
  columns: string[];
  data: Array<Record<string, string | number>>;
  max_week: number;
}

// ============================================================================
// Supplier Upload Tracking Types
// ============================================================================

export interface SupplierUploadTracking {
  id: number;
  customer: number;
  customer_name?: string;
  supplier: number;
  supplier_name?: string;
  supplier_email?: string;
  period_start: string;
  period_end: string;
  status: 'pending' | 'uploaded' | 'overdue';
  status_display?: string;
  last_upload?: number;
  uploaded_at?: string;
  file_name?: string;
  records_count: number;
  days_since_upload?: number;
  created_at: string;
  updated_at: string;
}

export interface UploadSummary {
  total_suppliers: number;
  uploaded: number;
  pending: number;
  overdue: number;
}

// ============================================================================
// API Methods
// ============================================================================

export const barrattAPI = {
  // Feature Flags
  getFeatureFlags: (): Promise<AxiosResponse<FeatureFlags>> =>
    api.get('/barratt/feature-flags/'),

  // ============================
  // Delivery Appeals
  // ============================
  getDeliveryAppeals: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<DeliveryAppeal>>> =>
    api.get('/barratt/delivery-appeals/', { params }),

  getDeliveryAppeal: (id: number): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.get(`/barratt/delivery-appeals/${id}/`),

  createDeliveryAppeal: (data: Partial<DeliveryAppeal>): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.post('/barratt/delivery-appeals/', data),

  updateDeliveryAppeal: (id: number, data: Partial<DeliveryAppeal>): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.patch(`/barratt/delivery-appeals/${id}/`, data),

  deleteDeliveryAppeal: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/delivery-appeals/${id}/`),

  submitDeliveryAppeal: (id: number): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.post(`/barratt/delivery-appeals/${id}/submit/`),

  reviewDeliveryAppeal: (id: number, data: {
    action: 'approve' | 'reject';
    reviewer_notes?: string;
    rejection_reason?: string;
  }): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.post(`/barratt/delivery-appeals/${id}/review/`, data),

  uploadDeliveryAppealAttachment: (id: number, formData: FormData): Promise<AxiosResponse<DeliveryAppealAttachment>> =>
    api.post(`/barratt/delivery-appeals/${id}/upload_attachment/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  getDeliveryAppealStats: (): Promise<AxiosResponse<DeliveryAppealStats>> =>
    api.get('/barratt/delivery-appeals/stats/'),

  // ============================
  // Spend Leakage
  // ============================
  getSpendLeakageReports: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<SpendLeakageReport>>> =>
    api.get('/barratt/spend-leakage/', { params }),

  getSpendLeakageReport: (id: number): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.get(`/barratt/spend-leakage/${id}/`),

  createSpendLeakageReport: (data: {
    title: string;
    description?: string;
    period_start: string;
    period_end: string;
  }): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.post('/barratt/spend-leakage/', data),

  updateSpendLeakageReport: (id: number, data: Partial<SpendLeakageReport>): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.patch(`/barratt/spend-leakage/${id}/`, data),

  deleteSpendLeakageReport: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/spend-leakage/${id}/`),

  publishSpendLeakageReport: (id: number): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.post(`/barratt/spend-leakage/${id}/publish/`),

  archiveSpendLeakageReport: (id: number): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.post(`/barratt/spend-leakage/${id}/archive/`),

  addSpendLeakageItem: (reportId: number, data: Partial<SpendLeakageItem>): Promise<AxiosResponse<SpendLeakageItem>> =>
    api.post(`/barratt/spend-leakage/${reportId}/add_item/`, data),

  recalculateSpendLeakageReport: (id: number): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.post(`/barratt/spend-leakage/${id}/recalculate/`),

  getSpendLeakageDashboard: (): Promise<AxiosResponse<SpendLeakageDashboard>> =>
    api.get('/barratt/spend-leakage/dashboard/'),

  // ============================
  // Commodity Codes
  // ============================
  getCommodityCodes: (params?: Record<string, string | number | boolean>): Promise<AxiosResponse<PaginatedResponse<CommodityCode>>> =>
    api.get('/barratt/commodity-codes/', { params }),

  getCommodityCode: (id: number): Promise<AxiosResponse<CommodityCode>> =>
    api.get(`/barratt/commodity-codes/${id}/`),

  createCommodityCode: (data: Partial<CommodityCode>): Promise<AxiosResponse<CommodityCode>> =>
    api.post('/barratt/commodity-codes/', data),

  updateCommodityCode: (id: number, data: Partial<CommodityCode>): Promise<AxiosResponse<CommodityCode>> =>
    api.patch(`/barratt/commodity-codes/${id}/`, data),

  deleteCommodityCode: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/commodity-codes/${id}/`),

  getCommodityCodeTree: (): Promise<AxiosResponse<CommodityCode[]>> =>
    api.get('/barratt/commodity-codes/tree/'),

  getCommodityCodeChildren: (id: number): Promise<AxiosResponse<CommodityCode[]>> =>
    api.get(`/barratt/commodity-codes/${id}/children/`),

  // ============================
  // Datasets (Barratt BI System)
  // ============================
  getDatasets: (params?: Record<string, string | number | boolean>): Promise<AxiosResponse<PaginatedResponse<Dataset>>> =>
    api.get('/barratt/datasets/', { params }),

  getDataset: (id: number): Promise<AxiosResponse<Dataset>> =>
    api.get(`/barratt/datasets/${id}/`),

  createDataset: (data: Partial<Dataset>): Promise<AxiosResponse<Dataset>> =>
    api.post('/barratt/datasets/', data),

  updateDataset: (id: number, data: Partial<Dataset>): Promise<AxiosResponse<Dataset>> =>
    api.patch(`/barratt/datasets/${id}/`, data),

  deleteDataset: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/datasets/${id}/`),

  getDatasetFields: (datasetId: number): Promise<AxiosResponse<DatasetField[]>> =>
    api.get(`/barratt/datasets/${datasetId}/fields/`),

  addDatasetField: (datasetId: number, data: Partial<DatasetField>): Promise<AxiosResponse<DatasetField>> =>
    api.post(`/barratt/datasets/${datasetId}/add_field/`, data),

  getDatasetRecords: (datasetId: number, params?: { page?: number; page_size?: number }): Promise<AxiosResponse<{
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
    results: DatasetRecord[];
  }>> =>
    api.get(`/barratt/datasets/${datasetId}/records/`, { params }),

  // ============================
  // Analyse Categories (Barratt Standalone)
  // ============================
  getCategories: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<AnalyseCategory>>> =>
    api.get('/barratt/categories/', { params }),

  getCategory: (id: number): Promise<AxiosResponse<AnalyseCategory>> =>
    api.get(`/barratt/categories/${id}/`),

  createCategory: (data: { name: string; hint?: string; sort_order?: number }): Promise<AxiosResponse<AnalyseCategory>> =>
    api.post('/barratt/categories/', data),

  updateCategory: (id: number, data: Partial<AnalyseCategory>): Promise<AxiosResponse<AnalyseCategory>> =>
    api.patch(`/barratt/categories/${id}/`, data),

  deleteCategory: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/categories/${id}/`),

  renameCategory: (id: number, name: string): Promise<AxiosResponse<AnalyseCategory>> =>
    api.post(`/barratt/categories/${id}/rename/`, { name }),

  // ============================
  // Analyse Dashboards
  // ============================
  getDashboards: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<AnalyseDashboard>>> =>
    api.get('/barratt/dashboards/', { params }),

  getDashboard: (id: number): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.get(`/barratt/dashboards/${id}/`),

  createDashboard: (data: Partial<AnalyseDashboard>): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.post('/barratt/dashboards/', data),

  updateDashboard: (id: number, data: Partial<AnalyseDashboard>): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.patch(`/barratt/dashboards/${id}/`, data),

  deleteDashboard: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/dashboards/${id}/`),

  shareDashboard: (id: number, sharedWith: string): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.post(`/barratt/dashboards/${id}/share/`, { shared_with: sharedWith }),

  // ============================
  // Dashboard Indicators
  // ============================
  getIndicators: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<DashboardIndicator>>> =>
    api.get('/barratt/indicators/', { params }),

  getIndicator: (id: number): Promise<AxiosResponse<DashboardIndicator>> =>
    api.get(`/barratt/indicators/${id}/`),

  createIndicator: (data: Partial<DashboardIndicator>): Promise<AxiosResponse<DashboardIndicator>> =>
    api.post('/barratt/indicators/', data),

  updateIndicator: (id: number, data: Partial<DashboardIndicator>): Promise<AxiosResponse<DashboardIndicator>> =>
    api.patch(`/barratt/indicators/${id}/`, data),

  deleteIndicator: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/indicators/${id}/`),

  // ============================
  // Division Performance
  // ============================
  getDivisionPerformanceScores: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<DivisionPerformanceScore>>> =>
    api.get('/barratt/division-performance/', { params }),

  getDivisionPerformanceScore: (id: number): Promise<AxiosResponse<DivisionPerformanceScore>> =>
    api.get(`/barratt/division-performance/${id}/`),

  getDivisionPerformanceYears: (): Promise<AxiosResponse<{ years: number[] }>> =>
    api.get('/barratt/division-performance/available_years/'),

  getDivisionPerformanceQuarters: (): Promise<AxiosResponse<{ quarters: QuarterInfo[] }>> =>
    api.get('/barratt/division-performance/available_quarters/'),

  getCurrentQuarter: (): Promise<AxiosResponse<{ quarter: string; year: number }>> =>
    api.get('/barratt/division-performance/current_quarter/'),

  saveDivisionPerformanceScore: (data: {
    supplier_id: number;
    score: number;
    comments?: string;
    quarter: string;
    year: number;
  }): Promise<AxiosResponse<DivisionPerformanceScore>> =>
    api.post('/barratt/division-performance/save_score/', data),

  completeQuarter: (quarter: string, year: number): Promise<AxiosResponse<QuarterCompletion>> =>
    api.post('/barratt/division-performance/complete_quarter/', { quarter, year }),

  reopenQuarter: (quarter: string, year: number): Promise<AxiosResponse<QuarterCompletion>> =>
    api.post('/barratt/division-performance/reopen_quarter/', { quarter, year }),

  getQuarterStatus: (quarter: string, year: number): Promise<AxiosResponse<{
    is_completed: boolean;
    quarter: string;
    year: number;
    completed_at?: string;
    completed_by?: string;
  }>> =>
    api.get('/barratt/division-performance/quarter_status/', { params: { quarter, year } }),

  getScoreReport: (params?: { year?: number; quarter?: string }): Promise<AxiosResponse<{
    year?: number;
    quarter?: string;
    data: ScoreReportRow[];
  }>> =>
    api.get('/barratt/division-performance/score_report/', { params }),

  // ============================
  // Call Off Performance
  // ============================
  getCallOffPerformance: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<CallOffPerformanceData>>> =>
    api.get('/barratt/calloff-performance/', { params }),

  getCallOffColumns: (): Promise<AxiosResponse<{ columns: string[]; max_week: number }>> =>
    api.get('/barratt/calloff-performance/columns/'),

  getCallOffAggregatedData: (params?: Record<string, string | number>): Promise<AxiosResponse<CallOffAggregatedData>> =>
    api.get('/barratt/calloff-performance/aggregated_data/', { params }),

  // ============================
  // Supplier Upload Tracking
  // ============================
  getSupplierUploads: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<SupplierUploadTracking>>> =>
    api.get('/barratt/supplier-uploads/', { params }),

  getSupplierUpload: (id: number): Promise<AxiosResponse<SupplierUploadTracking>> =>
    api.get(`/barratt/supplier-uploads/${id}/`),

  getSuppliersUploaded: (): Promise<AxiosResponse<SupplierUploadTracking[]>> =>
    api.get('/barratt/supplier-uploads/uploaded/'),

  getSuppliersNotUploaded: (): Promise<AxiosResponse<SupplierUploadTracking[]>> =>
    api.get('/barratt/supplier-uploads/not_uploaded/'),

  getUploadSummary: (): Promise<AxiosResponse<UploadSummary>> =>
    api.get('/barratt/supplier-uploads/summary/'),

  // ============================
  // Import Summaries (Barratt Data Upload)
  // ============================
  getImportSummaries: (params?: Record<string, string | number | boolean>): Promise<AxiosResponse<PaginatedResponse<ImportSummary>>> =>
    api.get('/barratt/import-summaries/', { params }),

  getImportSummary: (id: number): Promise<AxiosResponse<ImportSummary>> =>
    api.get(`/barratt/import-summaries/${id}/`),

  getImportSummaryLogs: (id: number): Promise<AxiosResponse<ImportLog[]>> =>
    api.get(`/barratt/import-summaries/${id}/logs/`),

  getImportSummaryExceptions: (id: number): Promise<AxiosResponse<ImportException[]>> =>
    api.get(`/barratt/import-summaries/${id}/exceptions/`),

  markImportComplete: (id: number): Promise<AxiosResponse<ImportSummary>> =>
    api.post(`/barratt/import-summaries/${id}/mark_complete/`),

  getImportTemplates: (): Promise<AxiosResponse<{ templates: ImportTemplate[] }>> =>
    api.get('/barratt/import-summaries/templates/'),

  uploadFile: (formData: FormData): Promise<AxiosResponse<ImportSummary>> =>
    api.post('/barratt/import-summaries/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  deleteImportSummary: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/import-summaries/${id}/`),
};
